import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { services } from "../../services";
import {
	DatasetStepEnum,
	type TDataRangeRequestObject,
} from "../../types/ledger.type";

import { LedgerActions } from "./ledgerSanitization.slice";

type TFileUploadObject = {
	file: File;
	datasetId: string;
	isStatic: boolean;
};

type TStaticColumnMappingRequestObject = {
	data: Record<string, any>;
	datasetId: string;
};

export const uploadFileThunk = createAsyncThunk(
	"ledgerSanitization/uploadFileThunk",
	async (
		fileUploadObject: TFileUploadObject,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const formData = new FormData();
			const meta = {};
			formData.append("file", fileUploadObject.file);
			formData.append("data", JSON.stringify(meta));
			const data = await services.ledgerService.fileUpload(
				formData,
				fileUploadObject.datasetId,
				fileUploadObject.isStatic
			);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`,
			});
		}
	}
);

export const getGeneralMetaDataThunk = createAsyncThunk(
	"ledgerSanitization/getGeneralMetaDataThunk",
	async (_, { dispatch, rejectWithValue }) => {
		try {
			const data =
				await services.ledgerService.getDatasetGeneralMeta();
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`,
			});
		}
	}
);

export const getDatasetMetaThunk = createAsyncThunk(
	"ledgerSanitization/getDatasetMetaThunk",
	async (
		datasetId: string,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.getDatasetMeta(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			const backendError = error.response?.data;
			const backendMessage =
				typeof backendError === "string"
					? backendError
					: backendError?.msg ??
					  backendError?.message ??
					  backendError?.error;
			return rejectWithValue({
				...(typeof backendError === "object" &&
				backendError !== null
					? backendError
					: {}),
				msg:
					backendMessage ??
					error.message ??
					"Unable to load dataset metadata.",
				status: error.response?.status,
			});
		}
	}
);

export const getStaticDataRangeThunk = createAsyncThunk(
	"ledgerSanitization/getStaticDataRange",
	async (
		datasetId: string,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.getStaticDataRange(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.msg}`,
				status: error.response.status,
			});
		}
	}
);

export const getPerfDataRangeThunk = createAsyncThunk(
	"ledgerSanitization/getPerfDataRange",
	async (
		datasetId: string,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.getPerfDataRange(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.msg}`,
				status: error.response.status,
			});
		}
	}
);

export const getStaticDataFromDataRange = createAsyncThunk(
	"ledgerSanitization/getStaticDataFromDataRange",
	async (
		reqObj: TDataRangeRequestObject,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.updateStaticDataRange(
					{
						is_step_confirmed: reqObj.isStepConfirmed,
						sheet_name: reqObj.sheet,
						header_range: reqObj.headerRange,
						body_range: reqObj.dataRange,
					},
					reqObj.datasetId
				);
			dispatch(
				LedgerActions.updateRangeSelection({
					sheet: reqObj.sheet,
					headerRange: reqObj.headerRange,
					dataRange: reqObj.dataRange,
					isStatic: true,
				})
			);
			if (reqObj.isReload) {
				toast.success("Data range preview updated.");
			}
			if (reqObj.isStepConfirmed) {
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_STATIC_DATA_RANGE,
						submissionCreated: true,
						isSubmissionSuccessful: true,
					})
				);
				return {
					table: null,
					...data,
				};
			} else {
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_STATIC_DATA_RANGE,
						submissionCreated: false,
						isSubmissionSuccessful: false,
					})
				);
			}
			return { table: data };
		} catch (error: any) {
			console.error("redux error", error);
			dispatch(
				LedgerActions.updateSanitizationSubmissionStatus({
					currentStep:
						DatasetStepEnum.STEP_STATIC_DATA_RANGE,
					submissionCreated: true,
					isSubmissionSuccessful: false,
					error: error.response.data,
				})
			);
			return rejectWithValue({
				...error.response,
				msg: `Error occurred: ${error.response.data.msg}`,
			});
		}
	}
);

export const getPerfDataFromDataRange = createAsyncThunk(
	"ledgerSanitization/getPerfDataFromDataRange",
	async (
		reqObj: TDataRangeRequestObject,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.updatePerfDataRange(
					{
						is_step_confirmed: reqObj.isStepConfirmed,
						sheet_name: reqObj.sheet,
						header_range: reqObj.headerRange,
						body_range: reqObj.dataRange,
					},
					reqObj.datasetId
				);
			dispatch(
				LedgerActions.updateRangeSelection({
					sheet: reqObj.sheet,
					headerRange: reqObj.headerRange,
					dataRange: reqObj.dataRange,
					isStatic: false,
				})
			);
			if (reqObj.isReload) {
				toast.success("Data range preview updated.");
			}
			if (reqObj.isStepConfirmed) {
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_PERF_DATA_RANGE,
						submissionCreated: true,
						isSubmissionSuccessful: true,
					})
				);
				return {
					table: null,
					...data,
				};
			} else {
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_PERF_DATA_RANGE,
						submissionCreated: false,
						isSubmissionSuccessful: false,
					})
				);
			}
			return { table: data };
		} catch (error: any) {
			console.error("redux error", error);
			dispatch(
				LedgerActions.updateSanitizationSubmissionStatus({
					currentStep: DatasetStepEnum.STEP_PERF_DATA_RANGE,
					submissionCreated: true,
					isSubmissionSuccessful: false,
					error: error.response.data,
				})
			);
			return rejectWithValue({
				...error.response,
				msg: `Error occurred: ${error.response.data.msg}`,
			});
		}
	}
);

export const getStaticColumnMappingDataThunk =
	createAsyncThunk(
		"ledgerSanitization/getStaticColumnMappingData",
		async (
			datasetId: string,
			{ dispatch, rejectWithValue }
		) => {
			try {
				const data =
					await services.ledgerService.getStaticColumns(
						datasetId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue({
					...error.response.data,
					msg: `Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`,
				});
			}
		}
	);

export const getPerfColumnMappingDataThunk =
	createAsyncThunk(
		"ledgerSanitization/getPerfColumnMappingData",
		async (
			datasetId: string,
			{ dispatch, rejectWithValue }
		) => {
			try {
				const data =
					await services.ledgerService.getPerfColumns(
						datasetId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue({
					...error.response.data,
					msg: `Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`,
				});
			}
		}
	);

export const updateStaticColumnMappingDataThunk =
	createAsyncThunk(
		"ledgerSanitization/updateStaticColumnMappingData",
		async (
			reqObj: TStaticColumnMappingRequestObject,
			{ dispatch, rejectWithValue }
		) => {
			try {
				const data =
					await services.ledgerService.updateStaticColumnMapping(
						reqObj.data,
						reqObj.datasetId
					);
				console.log(data);
				if (reqObj.data.is_step_confirmed) {
					dispatch(
						LedgerActions.updateSanitizationSubmissionStatus(
							{
								currentStep:
									DatasetStepEnum.STEP_STATIC_SANITIZATION,
								submissionCreated: true,
								isSubmissionSuccessful: true,
							}
						)
					);
				} else {
					// const sanitizationState = (getState() as any)
					// 	.sanitization;
					// const columnMapping =
					// 	sanitizationState.columnMapping.allColumns;
					// const columnNames = Object.keys(
					// 	reqObj.data.feedback
					// ).map(
					// 	(el) => columnMapping[el].column_metadata.name
					// );

					// toast.success(
					// 	`Column mapping for ${columnNames.join()} confirmed`
					// );
					dispatch(
						LedgerActions.updateSanitizationSubmissionStatus(
							{
								currentStep:
									DatasetStepEnum.STEP_STATIC_SANITIZATION,
								submissionCreated: false,
								isSubmissionSuccessful: false,
							}
						)
					);
				}
				return {
					response: data,
					isStepConfirmed: reqObj.data.is_step_confirmed,
				};
			} catch (error: any) {
				console.error("redux error", error);
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_STATIC_SANITIZATION,
						submissionCreated: true,
						isSubmissionSuccessful: false,
						error: error.response.data,
					})
				);
				toast.error(
					`Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`
				);
				return rejectWithValue({
					...error.response.data,
					msg: `Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`,
				});
			}
		}
	);

export const updatePerfColumnMappingDataThunk =
	createAsyncThunk(
		"ledgerSanitization/updatePerfColumnMappingData",
		async (
			reqObj: TStaticColumnMappingRequestObject,
			{ dispatch, rejectWithValue }
		) => {
			try {
				const data =
					await services.ledgerService.updatePerfColumnMapping(
						reqObj.data,
						reqObj.datasetId
					);
				if (reqObj.data.is_step_confirmed) {
					dispatch(
						LedgerActions.updateSanitizationSubmissionStatus(
							{
								currentStep:
									DatasetStepEnum.STEP_PERF_SANITIZATION,
								submissionCreated: true,
								isSubmissionSuccessful: true,
							}
						)
					);
				} else {
					// const sanitizationState = (getState() as any)
					// 	.sanitization;
					// const columnMapping =
					// 	sanitizationState.columnMapping.allColumns;
					// const columnNames = Object.keys(
					// 	reqObj.data.feedback
					// ).map(
					// 	(el) => columnMapping[el].column_metadata.name
					// );

					// toast.success(
					// 	`Column mapping for ${columnNames.join()} confirmed`
					// );
					dispatch(
						LedgerActions.updateSanitizationSubmissionStatus(
							{
								currentStep:
									DatasetStepEnum.STEP_PERF_SANITIZATION,
								submissionCreated: false,
								isSubmissionSuccessful: false,
							}
						)
					);
				}
				return {
					response: data,
					isStepConfirmed: reqObj.data.is_step_confirmed,
				};
			} catch (error: any) {
				console.error("redux error", error);
				dispatch(
					LedgerActions.updateSanitizationSubmissionStatus({
						currentStep:
							DatasetStepEnum.STEP_PERF_SANITIZATION,
						submissionCreated: true,
						isSubmissionSuccessful: false,
						error: error.response.data,
					})
				);
				toast.error(
					`Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`
				);
				return rejectWithValue({
					...error.response.data,
					msg: `Error occurred: ${error.response.data.error_code} - ${error.response.data.msg}`,
				});
			}
		}
	);

export const fetchDatasetSpecsQuestionsThunk =
	createAsyncThunk(
		"ledgerSanitization/fetchDatasetSpecsQuestionsThunk",
		async (datasetId: string, { rejectWithValue }) => {
			try {
				const data =
					await services.ledgerService.getDatasetSpecsQuestions(
						datasetId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue({
					...error.response.data,
					msg: `Error occurred: ${error.response.data.msg}`,
				});
			}
		}
	);

export const rerunDatasetStatsThunk = createAsyncThunk(
	"ledgerSanitization/rerunDatasetStatsThunk",
	async (
		reqObj: {
			datasetId: string;
			dashboardParams?: Record<string, any>;
		},
		{ rejectWithValue }
	) => {
		try {
			const data = await services.ledgerService.rerunDatasetStats(
				reqObj.datasetId,
				reqObj.dashboardParams ?? {}
			);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				error?.response?.data ?? error?.data ?? {}
			);
		}
	}
);

export const fetchDatasetStatsThunk = createAsyncThunk(
	"ledgerSanitization/fetchDatasetStatsThunk",
	async (datasetId: string, { rejectWithValue }) => {
		try {
			const data =
				await services.ledgerService.getDatasetStats(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			const responseData =
				error?.response?.data ?? error?.data ?? {};
			return rejectWithValue({
				...responseData,
				status:
					error?.response?.status ??
					error?.status ??
					null,
				statusText:
					error?.response?.statusText ??
					error?.statusText ??
					null,
				msg:
					responseData.msg ??
					responseData.error_message ??
					responseData.message ??
					error?.message ??
					"Unable to load review data.",
			});
		}
	}
);

export const confirmDatasetStatsThunk = createAsyncThunk(
	"ledgerSanitization/confirmDatasetStatsThunk",
	async (datasetId: string, { rejectWithValue }) => {
		try {
			const data =
				await services.ledgerService.confirmDatasetStats(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response.data,
				msg: `Error occurred: ${error.response.data.msg}`,
			});
		}
	}
);

export const getProcessingFiltersThunk = createAsyncThunk(
	"ledgerSanitization/getProcessingFilters",
	async (datasetId: string, { rejectWithValue }) => {
		try {
			const data =
				await services.ledgerService.getProcessingFilters(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response?.data,
				msg: `Error occurred: ${error.response?.data?.msg || error.message}`,
			});
		}
	}
);

type TAddProcessingFilterRequest = {
	datasetId: string;
	name: string;
	description: string;
	filterCode: string;
	filterType: string;
	order?: number;
	isUserConfirmed?: boolean;
	showToast?: boolean;
};

export const addProcessingFilterThunk = createAsyncThunk(
	"ledgerSanitization/addProcessingFilter",
	async (
		reqObj: TAddProcessingFilterRequest,
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.addProcessingFilter(
					reqObj.datasetId,
					{
						name: reqObj.name,
						description: reqObj.description,
						filter_code: reqObj.filterCode,
						filter_type: reqObj.filterType,
						order: reqObj.order,
						is_user_confirmed: reqObj.isUserConfirmed,
						}
					);

			if (data?.success === false) {
				const errorMsg = data?.error_message || "Error adding filter";
				if (reqObj.showToast !== false) {
					toast.error(`Error adding filter: ${errorMsg}`);
				}
				return rejectWithValue({
					...data,
					msg: errorMsg,
				});
			}

			if (reqObj.showToast !== false) {
				toast.success("Filter added successfully");
			}
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			if (reqObj.showToast !== false) {
				toast.error(
					`Error adding filter: ${error.response?.data?.msg || error.message}`
				);
			}
			return rejectWithValue({
				...error.response?.data,
				msg: `Error occurred: ${error.response?.data?.msg || error.message}`,
			});
		}
	}
);

type TUpdateProcessingFilterRequest = {
	datasetId: string;
	filterId: string;
	name?: string;
	description?: string;
	filterCode?: string;
	filterType?: string;
	order?: number;
	isUserConfirmed?: boolean;
	showToast?: boolean;
};

export const updateProcessingFilterThunk = createAsyncThunk(
	"ledgerSanitization/updateProcessingFilter",
	async (
		reqObj: TUpdateProcessingFilterRequest,
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.updateProcessingFilter(
					reqObj.datasetId,
					reqObj.filterId,
					{
						name: reqObj.name,
						description: reqObj.description,
						filter_code: reqObj.filterCode,
						filter_type: reqObj.filterType,
						order: reqObj.order,
						is_user_confirmed: reqObj.isUserConfirmed,
						}
					);

			if (data?.success === false) {
				const errorMsg = data?.error_message || "Error updating filter";
				if (reqObj.showToast !== false) {
					toast.error(`Error updating filter: ${errorMsg}`);
				}
				return rejectWithValue({
					...data,
					msg: errorMsg,
				});
			}

			if (reqObj.showToast !== false) {
				toast.success("Filter updated successfully");
			}
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			if (reqObj.showToast !== false) {
				toast.error(
					`Error updating filter: ${error.response?.data?.msg || error.message}`
				);
			}
			return rejectWithValue({
				...error.response?.data,
				msg: `Error occurred: ${error.response?.data?.msg || error.message}`,
			});
		}
	}
);

type TDeleteProcessingFilterRequest = {
	datasetId: string;
	filterId: string;
	filterType: string;
};

export const deleteProcessingFilterThunk = createAsyncThunk(
	"ledgerSanitization/deleteProcessingFilter",
	async (
		reqObj: TDeleteProcessingFilterRequest,
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.deleteProcessingFilter(
					reqObj.datasetId,
					reqObj.filterId,
					reqObj.filterType
				);
			toast.success("Filter deleted successfully");
			return { ...data, filterId: reqObj.filterId };
		} catch (error: any) {
			console.error("redux error", error);
			toast.error(
				`Error deleting filter: ${error.response?.data?.msg || error.message}`
			);
			return rejectWithValue({
				...error.response?.data,
				msg: `Error occurred: ${error.response?.data?.msg || error.message}`,
			});
		}
	}
);

// Perf Processing Filters Thunks
export const getPerfProcessingFiltersThunk = createAsyncThunk(
	"ledgerSanitization/getPerfProcessingFilters",
	async (datasetId: string, { rejectWithValue }) => {
		try {
			const data =
				await services.ledgerService.getPerfProcessingFilters(
					datasetId
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue({
				...error.response?.data,
				msg: `Error occurred: ${error.response?.data?.msg || error.message}`,
			});
		}
	}
);

type TAddPerfProcessingFilterRequest = {
	datasetId: string;
	name: string;
	description: string;
	filterCode: string;
	filterType: string;
	order?: number;
	isUserConfirmed?: boolean;
	showToast?: boolean;
};

export const addPerfProcessingFilterThunk = createAsyncThunk(
	"ledgerSanitization/addPerfProcessingFilter",
	async (
		reqObj: TAddPerfProcessingFilterRequest,
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.addPerfProcessingFilter(
					reqObj.datasetId,
					{
						name: reqObj.name,
						description: reqObj.description,
						filter_code: reqObj.filterCode,
						filter_type: reqObj.filterType,
						order: reqObj.order,
						is_user_confirmed: reqObj.isUserConfirmed,
						}
					);

			if (data?.success === false) {
				const errorMsg = data?.error_message || "Error adding filter";
				if (reqObj.showToast !== false) {
					toast.error(`Error adding filter: ${errorMsg}`);
				}
				return rejectWithValue({
					...data,
					msg: errorMsg,
				});
			}

			if (reqObj.showToast !== false) {
				toast.success("Filter added successfully");
			}
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			if (reqObj.showToast !== false) {
				toast.error(
					`Error adding filter: ${error.response?.data?.msg || error.message}`
				);
			}
			return rejectWithValue({
				...error.response?.data,
				msg: `Error occurred: ${error.response?.data?.msg || error.message}`,
			});
		}
	}
);

type TUpdatePerfProcessingFilterRequest = {
	datasetId: string;
	filterId: string;
	name?: string;
	description?: string;
	filterCode?: string;
	order?: number;
	isUserConfirmed?: boolean;
	filterType?: string;
	showToast?: boolean;
};

export const updatePerfProcessingFilterThunk = createAsyncThunk(
	"ledgerSanitization/updatePerfProcessingFilter",
	async (
		reqObj: TUpdatePerfProcessingFilterRequest,
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.updatePerfProcessingFilter(
					reqObj.datasetId,
					reqObj.filterId,
					{
						name: reqObj.name,
						description: reqObj.description,
						filter_code: reqObj.filterCode,
						order: reqObj.order,
						is_user_confirmed: reqObj.isUserConfirmed,
						filter_type: reqObj.filterType,
						}
					);

			if (data?.success === false) {
				const errorMsg = data?.error_message || "Error updating filter";
				if (reqObj.showToast !== false) {
					toast.error(`Error updating filter: ${errorMsg}`);
				}
				return rejectWithValue({
					...data,
					msg: errorMsg,
				});
			}

			if (reqObj.showToast !== false) {
				toast.success("Filter updated successfully");
			}
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			if (reqObj.showToast !== false) {
				toast.error(
					`Error updating filter: ${error.response?.data?.msg || error.message}`
				);
			}
			return rejectWithValue({
				...error.response?.data,
				msg: `Error occurred: ${error.response?.data?.msg || error.message}`,
			});
		}
	}
);

type TDeletePerfProcessingFilterRequest = {
	datasetId: string;
	filterId: string;
	filterType: string;
};

export const deletePerfProcessingFilterThunk = createAsyncThunk(
	"ledgerSanitization/deletePerfProcessingFilter",
	async (
		reqObj: TDeletePerfProcessingFilterRequest,
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.ledgerService.deletePerfProcessingFilter(
					reqObj.datasetId,
					reqObj.filterId,
					reqObj.filterType
				);
			toast.success("Filter deleted successfully");
			return { ...data, filterId: reqObj.filterId };
		} catch (error: any) {
			console.error("redux error", error);
			toast.error(
				`Error deleting filter: ${error.response?.data?.msg || error.message}`
			);
			return rejectWithValue({
				...error.response?.data,
				msg: `Error occurred: ${error.response?.data?.msg || error.message}`,
			});
		}
	}
);
